import { toast } from 'react-toastify';
import { Notification, NotificationParams } from '@core/models/notification.model';
import { IErrorModel, ISuccessResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import NotificationService from 'services/api/notification.service';

// -------------------------------------------------------------------------

interface INotificationState {
    loading: boolean;
    error: IErrorModel | undefined;
    notifications: Notification[];
    unreadCount: number;
}

const initialState: INotificationState = {
    loading: false,
    error: {} as IErrorModel,
    notifications: [],
    unreadCount: 0,
};

// -------------------------------------------------------------------------

export const getAllNotificationsThunk = createAsyncThunk<
    Notification[],
    NotificationParams | undefined,
    { rejectValue: IErrorModel }
>(`/notification/getAllNotifications`, async (params, { rejectWithValue }: any) => {
    try {
        const response: any = await NotificationService.getAllNotifications(params);
        return response.data;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to fetch notifications', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const markAsReadThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/notification/markAsRead`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await NotificationService.markAsRead(id);
            toast.success(response.data.message || 'Notification marked as read', { autoClose: 3000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to mark notification as read', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const markAllAsReadThunk = createAsyncThunk<ISuccessResponse, void, { rejectValue: IErrorModel }>(
    `/notification/markAllAsRead`,
    async (_, { rejectWithValue }: any) => {
        try {
            const response: any = await NotificationService.markAllAsRead();
            toast.success(response.data.message || 'All notifications marked as read', { autoClose: 3000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to mark all notifications as read', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const markAsUnreadThunk = createAsyncThunk<ISuccessResponse, number, { rejectValue: IErrorModel }>(
    `/notification/markAsUnread`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await NotificationService.markAsUnread(id);
            toast.success(response.data.message || 'Notification marked as unread', { autoClose: 3000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to mark notification as unread', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

// -------------------------------------------------------------------------

const notificationSlice = createSlice({
    name: `notification`,
    initialState,
    reducers: {
        clearNotifications(state) {
            state.notifications = [];
            state.unreadCount = 0;
        },
    },
    extraReducers: (builder) => {
        // Get All Notifications
        builder.addCase(getAllNotificationsThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getAllNotificationsThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.notifications = action.payload;
            state.unreadCount = action.payload.filter((n) => !n.isRead).length;
        });
        builder.addCase(getAllNotificationsThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Mark As Read
        builder.addCase(markAsReadThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(markAsReadThunk.fulfilled, (state, action) => {
            state.loading = false;
            const notification = state.notifications.find((n) => n.id === action.meta.arg);
            if (notification) {
                notification.isRead = true;
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            }
        });
        builder.addCase(markAsReadThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Mark All As Read
        builder.addCase(markAllAsReadThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(markAllAsReadThunk.fulfilled, (state) => {
            state.loading = false;
            state.notifications.forEach((notification) => {
                notification.isRead = true;
            });
            state.unreadCount = 0;
        });
        builder.addCase(markAllAsReadThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // Mark As Unread
        builder.addCase(markAsUnreadThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(markAsUnreadThunk.fulfilled, (state, action) => {
            state.loading = false;
            const notification = state.notifications.find((n) => n.id === action.meta.arg);
            if (notification) {
                notification.isRead = false;
                state.unreadCount += 1;
            }
        });
        builder.addCase(markAsUnreadThunk.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export default notificationSlice;
export const { clearNotifications } = notificationSlice.actions;
export const notificationReducer = notificationSlice.reducer;

